'use client';

import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/drawer';
import { useQuery, useZero } from '@rocicorp/zero/react';
import { FC, FormEvent, useState } from 'react';
import Link from 'next/link';
import { formatQuantityForDisplay } from '@utils/measurement-system';
import { runZeroMutation } from '~/src/zero/run-mutation';
import { useSheetStackParams } from '@hooks/use-sheet-stack-params';
import { queries } from '~/src/zero/queries';
import { useEffectiveMeasurementSystem } from '~/src/zero/use-measurement-system';
import AttachmentGallery from '@components/files/AttachmentGallery';
import { fileRowsToViews } from '@files/file-row';
import { useTranslations } from 'next-intl';

type RecipeDrawerProps = {
  organization_id: string;
  canEditRecipes: boolean;
};

/**
 * Mobile drawer generalizing CocktailCard.tsx's "tap card -> Drawer with
 * more info" pattern for menu->recipe. Pairs with the sibling IngredientSheet
 * (recipe->ingredient, mounted alongside this one in RecipeSheetStack) --
 * both read their state from useSheetStackParams independently, so a deep
 * link reconstructs the whole stack and the back button pops it one level
 * at a time.
 *
 * Instructions/Notes are only editable (inline, saved on blur) when the
 * viewer has `update` permission on recipes (`canEditRecipes`); otherwise
 * they render as plain read-only text and no mutation is ever wired up.
 */
const RecipeDrawer: FC<RecipeDrawerProps> = ({
  organization_id,
  canEditRecipes
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const z = useZero() as any;
  const filesT = useTranslations('files');
  const compositionT = useTranslations('menu-import.composition');
  const { recipeId, openIngredient, closeRecipe } = useSheetStackParams();
  const [savingField, setSavingField] = useState<string | null>(null);
  const measurementSystem = useEffectiveMeasurementSystem(organization_id);

  const [recipeRowUntyped] = useQuery(
    recipeId ? queries.recipes.withIngredients({ recipeId }) : undefined
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recipeRow = recipeRowUntyped as any;
  const links = recipeRow?.ingredientLinks ?? [];
  const recipeFiles = fileRowsToViews(recipeRow?.files);

  const open = !!recipeId;

  const saveField = async (
    field: 'instructions' | 'notes',
    e: FormEvent<HTMLTextAreaElement>
  ) => {
    if (!recipeRow) {
      return;
    }
    setSavingField(field);
    await runZeroMutation(
      z.mutate.recipes.update({
        id: recipeRow.id,
        name: recipeRow.name,
        subtitle: recipeRow.subtitle,
        instructions:
          field === 'instructions'
            ? e.currentTarget.value
            : recipeRow.instructions,
        notes: field === 'notes' ? e.currentTarget.value : recipeRow.notes,
        now: Date.now()
      }),
      { errorTitle: 'Could not save recipe' }
    );
    setSavingField(null);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          closeRecipe();
        }
      }}
    >
      <DrawerContent>
        <div className="mx-auto w-full max-w-md pb-6">
          <DrawerHeader>
            <DrawerTitle>{recipeRow?.name ?? 'Recipe'}</DrawerTitle>
          </DrawerHeader>

          <div className="max-h-[70vh] overflow-y-auto px-4">
            <section className="mb-4">
              <h3 className="mb-2 text-label text-muted-foreground">
                Ingredients
              </h3>
              <ul className="divide-y divide-border rounded-md border">
                {links.map((link: any) => {
                  const preparation = link.ingredient?.preparationRecipe;
                  const content = (
                    <>
                      <span className="text-body-dense">
                        <span
                          className={
                            preparation
                              ? 'text-brand-text underline underline-offset-4'
                              : undefined
                          }
                        >
                          {link.ingredient?.fullName}
                        </span>
                        {preparation && (
                          <span className="block text-caption text-muted-foreground">
                            {compositionT(
                              preparation.preparationInferred &&
                                preparation.status === 'draft'
                                ? 'inferred'
                                : 'linked'
                            )}
                          </span>
                        )}
                      </span>
                      <span className="text-body-dense text-muted-foreground tabular-nums">
                        {formatQuantityForDisplay(
                          link.quantity,
                          link.quantityStr,
                          link.quantityType,
                          measurementSystem
                        )}
                      </span>
                    </>
                  );
                  const className =
                    'flex w-full items-center justify-between gap-4 p-3 text-left min-h-11 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
                  return (
                    <li key={link.id}>
                      {preparation ? (
                        <Link
                          href={`/${organization_id}/bev/recipes/${preparation.id}`}
                          className={className}
                        >
                          {content}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openIngredient(link.id)}
                          className={className}
                        >
                          {content}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="mb-4">
              <label
                htmlFor="drawer-instructions"
                className="mb-2 block text-label text-muted-foreground"
              >
                Instructions
                {canEditRecipes && savingField === 'instructions'
                  ? ' (saving…)'
                  : ''}
              </label>
              {canEditRecipes ? (
                <Textarea
                  id="drawer-instructions"
                  defaultValue={recipeRow?.instructions ?? ''}
                  onBlur={e => saveField('instructions', e)}
                  rows={4}
                />
              ) : (
                <p
                  id="drawer-instructions"
                  className="whitespace-pre-wrap text-body"
                >
                  {recipeRow?.instructions ?? ''}
                </p>
              )}
            </section>

            <section className="mb-4">
              <label
                htmlFor="drawer-notes"
                className="mb-2 block text-label text-muted-foreground"
              >
                Notes
                {canEditRecipes && savingField === 'notes' ? ' (saving…)' : ''}
              </label>
              {canEditRecipes ? (
                <Textarea
                  id="drawer-notes"
                  defaultValue={recipeRow?.notes ?? ''}
                  onBlur={e => saveField('notes', e)}
                  rows={3}
                />
              ) : (
                <p id="drawer-notes" className="whitespace-pre-wrap text-body">
                  {recipeRow?.notes ?? ''}
                </p>
              )}
            </section>

            {recipeFiles.length > 0 ? (
              <section className="mb-4">
                <h3 className="mb-2 text-label text-muted-foreground">
                  {filesT('title')}
                </h3>
                <AttachmentGallery files={recipeFiles} />
              </section>
            ) : null}

            {recipeRow ? (
              <Link
                href={`/${organization_id}/bev/recipes/${recipeRow.id}/edit`}
              >
                <Button variant="outline" className="w-full">
                  Open full editor
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default RecipeDrawer;
